import { useCallback, useMemo, useState } from 'react';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  COVERAGE_TABLE,
  COVERAGE_TABLE_FILTERS_QUERY,
} from '@elementary/common-ui/constants/queryKeys';
import {
  tableAssetsCoverageFilterOptionsQuery,
  tableAssetsCoverageQuery,
} from 'catalog/services/api';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { useCSVExport } from 'shared/hooks/useCSVExport';
import {
  COVERAGE_TABLE_FILTERS,
  COVERAGE_TABLE_SORT,
} from '@elementary/common-ui/constants/urlParams';
import { PAGE_SIZE } from '@elementary/common-ui/constants';
import { SortDirection } from '@elementary/common-ui/components';
import {
  TableAssetsFiltersQueryResponse,
  TableAssetsQueryRequest,
  TableAssetsQueryResponse,
} from '../types';

const CatalogCsvHeaders = [
  { id: 'name', title: 'Asset name' },
  { id: 'coverage.percentageAmount', title: 'Coverage' },
  { id: 'coverage.freshness', title: 'Freshness' },
  { id: 'coverage.completeness', title: 'Completeness' },
  { id: 'coverage.uniqueness', title: 'Uniqueness' },
  { id: 'coverage.validity', title: 'Validity' },
  { id: 'coverage.accuracy', title: 'Accuracy' },
  { id: 'coverage.consistency', title: 'Consistency' },
  { id: 'coverage.other', title: 'Other' },
];

const filterMap: Record<
  string,
  (val: string[]) => Partial<TableAssetsQueryRequest['filters']>
> = {
  ids: (val) => ({ ids: val }),
  owners: (val) => ({ owners: val }),
  tags: (val) => ({ tags: val }),
  name: (val) => ({ names: val }),
  dbName: (val) => ({ dbNames: val }),
  schemaName: (val) => ({ schemaNames: val }),
  testNames: (val) => ({ testNames: val }),
  isCritical: (val) => ({ isCritical: val[0] === 'true' }),
  upstreamFrom: (val) => ({ upstreamFrom: val }),
  downstreamFrom: (val) => ({ downstreamFrom: val }),
  'coverage.accuracy': (val: string[]) => ({
    dimensionTestNames: {
      accuracy: val,
    },
  }),
  'coverage.completeness': (val: string[]) => ({
    dimensionTestNames: {
      completeness: val,
    },
  }),
  'coverage.consistency': (val: string[]) => ({
    dimensionTestNames: {
      consistency: val,
    },
  }),
  'coverage.freshness': (val: string[]) => ({
    dimensionTestNames: {
      freshness: val,
    },
  }),
  'coverage.uniqueness': (val: string[]) => ({
    dimensionTestNames: {
      uniqueness: val,
    },
  }),
  'coverage.validity': (val: string[]) => ({
    dimensionTestNames: {
      validity: val,
    },
  }),
  'coverage.other': (val: string[]) => ({
    dimensionTestNames: {
      other: val,
    },
  }),
  'coverage.percentageAmount': (val: string[]) => {
    const mapped = val.map((item) => {
      const [min, max] = item
        .replaceAll('%', '')
        .split('-')
        .map((v) => Number(v));
      return { min, max };
    });

    return { percentageAmount: mapped };
  },
};

const mergeFilters = (
  mappedFilters: Partial<TableAssetsQueryRequest['filters']>[],
): TableAssetsQueryRequest['filters'] =>
  mappedFilters.reduce((acc, curr) => {
    if (!curr) return acc;
    if (curr.dimensionTestNames) {
      return {
        ...acc,
        dimensionTestNames: {
          ...(acc?.dimensionTestNames || {}),
          ...curr.dimensionTestNames,
        },
      };
    }
    return { ...acc, ...curr };
  }, {} as TableAssetsQueryRequest['filters']);

const sortFieldMap: Record<
  string,
  NonNullable<TableAssetsQueryRequest['sort']>['field']
> = {
  name: 'name',
  'coverage.percentageAmount': 'percentage_amount',
  'coverage.freshness': 'freshness_test_count',
  'coverage.completeness': 'completeness_test_count',
  'coverage.uniqueness': 'uniqueness_test_count',
  'coverage.validity': 'validity_test_count',
  'coverage.accuracy': 'accuracy_test_count',
  'coverage.consistency': 'consistency_test_count',
  'coverage.other': 'other_test_count',
};

type Props = { search?: string; daysBack: number };

export const useCoverageTableData = ({ search, daysBack }: Props) => {
  const selectedEnvId = useSelectedEnv();
  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();
  const { treeViewBy } = useTreeViewParams();
  const { urlFilters } = useUrlFilters({ urlKey: COVERAGE_TABLE_FILTERS });
  const urlFiltersDebounced = useDebounce(urlFilters, 1000);
  const searchDebounced = useDebounce(search, 500);

  const { exportToCSV } = useCSVExport({
    headers: CatalogCsvHeaders,
    headerIdDelimiter: '.',
  });

  const [searchParams] = useSearchParams();

  const urlSort: { id: string; direction: SortDirection } = useMemo(
    () => JSON.parse(searchParams.get(COVERAGE_TABLE_SORT) || 'null'),
    [searchParams],
  );

  const getQueryVariables = useCallback(
    (pageParam: number): TableAssetsQueryRequest => {
      const mappedFilters =
        urlFiltersDebounced?.map((filter) => {
          const key =
            filter.property === 'lineage' ? filter.operator : filter.property;
          const mapper = filterMap[key];
          return mapper ? mapper(filter.value) : undefined;
        }) || [];

      const filters = mergeFilters(mappedFilters) || {};

      if (selectedTreeFolderNodes?.length) {
        const nonTestNodes = selectedTreeFolderNodes.filter(
          (node) => node.resourceType !== 'test',
        );
        if (nonTestNodes.length) {
          filters.ids = nonTestNodes.map((node) => node.id);
        }
      }

      if (searchDebounced) {
        filters.search = searchDebounced;
      }

      return {
        offset: (pageParam - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        filters,
        sort: urlSort
          ? {
              field: sortFieldMap[urlSort.id],
              direction: urlSort.direction.toLowerCase() as 'asc' | 'desc',
            }
          : undefined,
      };
    },
    [searchDebounced, selectedTreeFolderNodes, urlFiltersDebounced, urlSort],
  );

  const { data, isFetching, fetchNextPage, isLoading } =
    useInfiniteQuery<TableAssetsQueryResponse>({
      queryKey: [
        COVERAGE_TABLE,
        selectedEnvId,
        selectedTreeFolderNodes?.map((node) => node.id),
        urlFiltersDebounced,
        urlSort,
        searchDebounced,
        daysBack,
      ],
      queryFn: ({ pageParam = 1 }) =>
        tableAssetsCoverageQuery(
          getQueryVariables(pageParam),
          selectedEnvId!,
          daysBack,
        ),
      getNextPageParam: (lastPage, allPages) => {
        const {
          pagination: { hasNextPage },
        } = lastPage;

        if (!hasNextPage) {
          return undefined;
        }

        const allItemsCount = allPages
          .map((page) => page.tableAssets)
          .flat()?.length;

        const lastPageNumber = Math.ceil(
          allItemsCount ? allItemsCount / PAGE_SIZE : 1,
        );
        return lastPageNumber + 1;
      },
      enabled:
        !!selectedEnvId &&
        (selectedTreeNode?.resourceType === 'folder' || !selectedTreeNode),
    });

  const getFilterOptionsQueryArgs = useMemo(() => {
    if (selectedTreeFolderNodes?.length) {
      const nonTestNodes = selectedTreeFolderNodes.filter(
        (node) => node.resourceType !== 'test',
      );
      if (nonTestNodes.length) {
        return {
          filters: { ids: nonTestNodes.map((node) => node.id) },
        };
      }
    }

    return {};
  }, [selectedTreeFolderNodes]);

  const { data: filtersData, isFetching: isFiltersFetching } =
    useQuery<TableAssetsFiltersQueryResponse>({
      queryKey: [
        COVERAGE_TABLE_FILTERS_QUERY,
        selectedEnvId,
        treeViewBy,
        selectedTreeNode?.id,
        daysBack,
      ],
      queryFn: () =>
        tableAssetsCoverageFilterOptionsQuery(
          getFilterOptionsQueryArgs,
          selectedEnvId!,
          daysBack,
        ),
      enabled:
        !!selectedEnvId &&
        (selectedTreeNode?.resourceType === 'folder' || !selectedTreeNode),
    });

  const allItems = useMemo(
    () => data?.pages.map((page) => page.tableAssets).flat(),
    [data],
  );

  const [isExporting, setIsExporting] = useState(false);

  const mapCoverageToCsvData = useCallback(
    (items: TableAssetsQueryResponse['tableAssets']) =>
      items.map((record) => ({
        name: record.name,
        coverage: {
          percentageAmount: record.coverage.percentageAmount,
          freshness: JSON.stringify(record.coverage.freshness),
          completeness: JSON.stringify(record.coverage.completeness),
          uniqueness: JSON.stringify(record.coverage.uniqueness),
          validity: JSON.stringify(record.coverage.validity),
          accuracy: JSON.stringify(record.coverage.accuracy),
          consistency: JSON.stringify(record.coverage.consistency),
          other: JSON.stringify(record.coverage.other),
        },
      })),
    [],
  );

  const onCSVDownload = useCallback(async () => {
    if (!selectedEnvId) return;
    setIsExporting(true);
    try {
      const allExportItems: TableAssetsQueryResponse['tableAssets'] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await tableAssetsCoverageQuery(
          getQueryVariables(page),
          selectedEnvId,
          daysBack,
        );
        allExportItems.push(...response.tableAssets);
        hasMore = response.pagination.hasNextPage;
        page += 1;
      }

      const csvData = mapCoverageToCsvData(allExportItems);
      exportToCSV(csvData as Record<string, any>[], 'coverage.csv');
    } finally {
      setIsExporting(false);
    }
  }, [
    selectedEnvId,
    getQueryVariables,
    daysBack,
    mapCoverageToCsvData,
    exportToCSV,
  ]);

  const totalItems = useMemo(
    () => data?.pages[0]?.pagination?.totalItemsCount || 0,
    [data],
  );

  const totalCritical = useMemo(
    () =>
      filtersData?.criticality?.find(
        (item: { value: string; count: number }) => item.value === 'true',
      )?.count || 0,
    [filtersData],
  );

  return {
    data: allItems || [],
    isLoading,
    isFetching,
    fetchNextPage,
    onCSVDownload,
    isExporting,
    totalItems,
    totalCritical,
    isFiltersFetching,
  };
};
